import { Speech } from './speech'

const STORAGE_KEY = 'moisiltm-a11y'
const TOGGLES = ['contrast', 'lightbg', 'invert', 'grayscale', 'underline', 'readable']
// Color modes are mutually exclusive — enabling one clears the others.
const EXCLUSIVE = {
  contrast: ['lightbg', 'invert'],
  lightbg: ['contrast', 'invert'],
  invert: ['contrast', 'lightbg'],
}
const FONT_MIN = -2 // 75%
const FONT_MAX = 4 // 150%
const FONT_STEP = 12.5 // percent per step

export class Accessibility {
  constructor() {
    this.root = document.getElementById('a11y-toolbar')
    if (!this.root) return

    this.html = document.documentElement
    this.trigger = this.root.querySelector('.a11y-trigger')
    this.panel = this.root.querySelector('.a11y-panel')
    this.prefs = this.load()
    this.apply()

    this.trigger.addEventListener('click', () => this.togglePanel())

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen()) {
        this.togglePanel(false)
        this.trigger.focus()
      }
    })

    document.addEventListener('click', (e) => {
      if (this.isOpen() && !this.root.contains(e.target)) this.togglePanel(false)
    })

    this.root.querySelectorAll('[data-a11y]').forEach((btn) => {
      btn.addEventListener('click', () => this.action(btn.dataset.a11y))
    })

    this.setupSpeech()
  }

  setupSpeech() {
    this.speakBtn = this.root.querySelector('[data-a11y="speak"]')
    if (!this.speakBtn) return

    // Hide the control entirely where the browser can't deliver it.
    if (!Speech.supported) {
      this.speakBtn.hidden = true
      return
    }

    this.speakLabel = this.speakBtn.querySelector('.a11y-btn-label')
    this.speech = new Speech({
      onStateChange: (speaking) => {
        this.speakBtn.setAttribute('aria-pressed', speaking)
        this.speakBtn.classList.toggle('is-on', speaking)
        if (this.speakLabel) {
          this.speakLabel.textContent = speaking ? 'Oprește citirea' : 'Citește pagina'
        }
      },
    })
  }

  isOpen() {
    return this.root.classList.contains('is-open')
  }

  togglePanel(force) {
    const open = typeof force === 'boolean' ? force : !this.isOpen()
    this.root.classList.toggle('is-open', open)
    this.trigger.setAttribute('aria-expanded', open)
    if (open) {
      const first = this.panel.querySelector('[data-a11y]')
      if (first) first.focus()
    }
  }

  action(name) {
    // Reading state is per-visit, never persisted — handle it before prefs.
    if (name === 'speak') {
      if (this.speech) this.speech.toggle()
      return
    }

    if (name === 'font-up') {
      this.prefs.fontStep = Math.min(FONT_MAX, (this.prefs.fontStep || 0) + 1)
    } else if (name === 'font-down') {
      this.prefs.fontStep = Math.max(FONT_MIN, (this.prefs.fontStep || 0) - 1)
    } else if (name === 'reset') {
      this.prefs = {}
      if (this.speech) this.speech.stop()
    } else if (TOGGLES.includes(name)) {
      this.prefs[name] = !this.prefs[name]
      if (this.prefs[name] && EXCLUSIVE[name]) {
        EXCLUSIVE[name].forEach((other) => delete this.prefs[other])
      }
    }
    this.save()
    this.apply()
  }

  apply() {
    TOGGLES.forEach((key) => {
      this.html.classList.toggle(`a11y-${key}`, !!this.prefs[key])
    })

    const step = this.prefs.fontStep || 0
    this.html.style.fontSize = step === 0 ? '' : `${100 + step * FONT_STEP}%`

    // Reflect state on the buttons.
    this.root.querySelectorAll('[data-a11y]').forEach((btn) => {
      const name = btn.dataset.a11y
      if (TOGGLES.includes(name)) {
        btn.setAttribute('aria-pressed', !!this.prefs[name])
        btn.classList.toggle('is-on', !!this.prefs[name])
      } else if (name === 'font-up') {
        btn.disabled = step >= FONT_MAX
      } else if (name === 'font-down') {
        btn.disabled = step <= FONT_MIN
      }
    })
  }

  load() {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY)) || {}
    } catch (e) {
      return {}
    }
  }

  save() {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.prefs))
    } catch (e) {
      /* private mode etc. — preferences just won't persist */
    }
  }
}
